import {
  Filter,
  Task,
  FilterCondition,
  FilterQuery,
  FilterOperator,
  GanttInstance
} from './types';
import { transformHourToDays } from '../../../views/ganttContainer/gantt/gantt.helper.js';
import { getTaskDate, HOUR_TO_DAY_FIELDS, isNullOrUndefined } from './utils.ts';

export const clearMarks = (
  gantt: GanttInstance,
  mark: 'filtered' | 'previewed'
): void =>
  gantt.eachTask((task: Task) => {
    task[mark] = false;
  });

export const normalizeFilter = (filter: Filter): FilterCondition | null => {
  if (!filter.equals) {
    return null;
  }

  if (isNullOrUndefined(filter.filter_by) || filter.filter_by === '') {
    return null;
  }

  if (Array.isArray(filter.filter_by) && filter.filter_by.length === 0) {
    return null;
  }

  return {
    field: filter.name,
    operator: filter.equals,
    value: filter.filter_by,
    dataType: filter.data_type,
    extractFrom: filter.el_to_extract_from
  };
};

export const buildFilterQuery = (
  filters: Filter[],
  useOrLogic: boolean = false
): FilterQuery => {
  const conditions = filters
    .map(normalizeFilter)
    .filter((condition): condition is FilterCondition => condition !== null);

  return {
    conditions,
    logicalOperator: useOrLogic ? 'OR' : 'AND'
  };
};

export const evaluateDateFilter = (
  taskValue: string | Date,
  operator: FilterOperator,
  filterValue: string
): boolean => {
  const taskDate = new Date(taskValue);
  const filterDate = new Date(filterValue);

  switch (operator) {
    case 'afterthan':
      return taskDate.getTime() > filterDate.getTime();
    case 'beforethan':
      return taskDate.getTime() < filterDate.getTime();
    default:
      return false;
  }
};

// Special evaluation function for weekly range overlap (used for preview mode)
export const evaluateWeeklyRangeOverlap = (
  task: Task,
  filterValue: string
): boolean => {
  const [weekStart, weekEnd] = filterValue.split('|');
  const startDate = getTaskDate(task.start_date);
  const endDate = getTaskDate(task.end_date);

  return (
    (startDate >= weekStart && endDate <= weekEnd) ||
    (startDate <= weekStart && endDate >= weekEnd) ||
    (startDate <= weekStart && endDate >= weekStart) ||
    (startDate <= weekEnd && endDate >= weekEnd)
  );
};

export const evaluateStringFilter = (
  taskValue: Array<Record<string, string>> | unknown,
  operator: FilterOperator,
  filterValue: string,
  extractFrom?: string
): boolean => {
  let stringValue: string;

  if (Array.isArray(taskValue)) {
    if (extractFrom && taskValue.length > 0) {
      const extractedValues = taskValue
        .map((item) => item[extractFrom])
        .filter(Boolean);
      stringValue = extractedValues.join(' ').toLowerCase();
    } else {
      stringValue = taskValue
        .map((item) => String(item))
        .join(' ')
        .toLowerCase();
    }
  } else {
    stringValue = String(taskValue || '').toLowerCase();
  }

  const lowerFilterValue = filterValue.toLowerCase();

  switch (operator) {
    case 'includes':
      return stringValue.includes(lowerFilterValue);
    case 'notincludes':
      return !stringValue.includes(lowerFilterValue);
    case 'is':
      return stringValue === lowerFilterValue;
    case 'isnot':
      return stringValue !== lowerFilterValue;
    default:
      return false;
  }
};

export const evaluateNumberFilter = (
  taskValue: number,
  operator: FilterOperator,
  filterValue: string | number
): boolean => {
  const numValue = Number(taskValue) || 0;
  const numFilter = Number(filterValue) || 0;

  switch (operator) {
    case 'equals':
      return numValue === numFilter;
    case 'notequals':
      return numValue !== numFilter;
    case 'morethan':
      return numValue > numFilter;
    case 'lessthan':
      return numValue < numFilter;
    case 'moreequalsthan':
      return numValue >= numFilter;
    case 'lessequalsthan':
      return numValue <= numFilter;
    default:
      return false;
  }
};

export const evaluateArrayFilter = (
  taskValue: any[],
  operator: FilterOperator,
  filterValue: any[],
  extractFrom?: string
): boolean => {
  if (!Array.isArray(taskValue)) {
    return false;
  }

  const values = extractFrom
    ? taskValue.map((item) => item[extractFrom]).filter(Boolean)
    : taskValue;

  const hasMatch = values.some((value) =>
    filterValue.some((filterVal) => String(value) === String(filterVal))
  );

  return operator === 'issomeof' ? hasMatch : !hasMatch;
};

export const evaluateOverdueStatus = (task: Task): boolean =>
  (task.progress || 0) < (task.expected || 0);

export const evaluateAdvancementStatus = (task: Task): boolean =>
  (task.progress || 0) > (task.expected || 0);

export const evaluateRegularStatuses = (
  task: Task,
  statuses: string[]
): boolean => statuses.includes(String(task.status));

export const combineStatusResults = (
  results: boolean[],
  operator: FilterOperator
): boolean =>
  operator === 'issomeof'
    ? results.some((result) => result)
    : results.every((result) => result);

export const evaluateStatusFilter = (
  task: Task,
  operator: FilterOperator,
  filterValue: string | string[]
): boolean => {
  const values = Array.isArray(filterValue)
    ? filterValue
    : filterValue.split(',');

  const results: boolean[] = [];

  if (values.includes('Overdue')) {
    results.push(evaluateOverdueStatus(task));
  }

  if (values.includes('Advancement')) {
    results.push(evaluateAdvancementStatus(task));
  }

  const regularStatuses = values.filter(
    (val) => val !== 'Overdue' && val !== 'Advancement'
  );

  if (regularStatuses.length > 0) {
    results.push(evaluateRegularStatuses(task, regularStatuses));
  }

  return results.length > 0 ? combineStatusResults(results, operator) : false;
};

const transformTaskValue = (field: string, taskValue: any): any => {
  if (HOUR_TO_DAY_FIELDS.includes(field) && !isNullOrUndefined(taskValue)) {
    return transformHourToDays(taskValue);
  }
  return taskValue;
};

const normalizeToArray = (taskValue: any): any[] => {
  if (Array.isArray(taskValue)) {
    return taskValue;
  }

  if (isNullOrUndefined(taskValue)) {
    return [];
  }

  return [taskValue];
};

export const evaluateArrayCondition = (
  taskValue: any,
  condition: FilterCondition
): boolean => {
  const { operator, value, extractFrom } = condition;
  const arrayValue = normalizeToArray(taskValue);
  const filterValues = Array.isArray(value) ? value : [value];
  return evaluateArrayFilter(arrayValue, operator, filterValues, extractFrom);
};

export const evaluateDefaultCondition = (
  taskValue: any,
  condition: FilterCondition
): boolean =>
  evaluateStringFilter(
    taskValue,
    condition.operator,
    String(condition.value),
    condition.extractFrom
  );

const isStatusFilter = (condition: FilterCondition): boolean =>
  condition.field === 'status' && condition.dataType !== 'array/images';

export const evaluateFilterCondition = (
  task: Task,
  condition: FilterCondition
): boolean => {
  if (condition.field === 'preview_mode') {
    return evaluateWeeklyRangeOverlap(task, String(condition.value));
  }

  let taskValue = task[condition.field];
  taskValue = transformTaskValue(condition.field, taskValue);

  if (isNullOrUndefined(taskValue)) {
    return false;
  }

  if (isStatusFilter(condition)) {
    return evaluateStatusFilter(task, condition.operator, condition.value);
  }

  switch (condition.dataType) {
    case 'date':
      return evaluateDateFilter(
        taskValue,
        condition.operator,
        String(condition.value)
      );

    case 'string':
      return evaluateStringFilter(
        taskValue,
        condition.operator,
        String(condition.value),
        condition.extractFrom
      );

    case 'number':
      return evaluateNumberFilter(
        Number(taskValue),
        condition.operator,
        condition.value
      );

    case 'array/string':
    case 'array/icon':
    case 'array/images':
      return evaluateArrayCondition(taskValue, condition);

    default:
      return evaluateDefaultCondition(taskValue, condition);
  }
};

export const evaluateFilterQuery = (
  task: Task,
  query: FilterQuery
): boolean => {
  if (query.conditions.length === 0) return true;

  const results = query.conditions.map((condition) =>
    evaluateFilterCondition(task, condition)
  );

  return query.logicalOperator === 'OR'
    ? results.some((result) => result)
    : results.every((result) => result);
};

export const hasChildren = (
  gantt: GanttInstance,
  taskId: string | number
): boolean => gantt.hasChild(taskId);

export const getParentId = (
  gantt: GanttInstance,
  taskId: string | number
): string | number | null => {
  const task = gantt.getTask(taskId);
  return task ? task.parent : null;
};

export const getChildrenIds = (
  gantt: GanttInstance,
  taskId: string | number
): (string | number)[] => gantt.getChildren(taskId);

export const applyHierarchicalFilters = (
  gantt: GanttInstance,
  query: FilterQuery,
  marker: 'previewed' | 'filtered' = 'filtered'
): void => {
  const taskFilterResults = new Map<string | number, boolean>();
  const taskHasVisibleDescendants = new Map<string | number, boolean>();

  const allTasks: Task[] = gantt.getTaskByTime().toSorted((a, b) => {
    // Sort by hierarchy level in reverse order (deepest first)
    const levelDiff = (b.$level || 0) - (a.$level || 0);
    if (levelDiff !== 0) return levelDiff;
    // Then by task index for stable ordering within same level
    return (a.$index || 0) - (b.$index || 0);
  });

  allTasks.forEach((task) => {
    const passesFilter = evaluateFilterQuery(task, query);
    taskFilterResults.set(task.id, passesFilter);
  });

  allTasks.forEach((task) => {
    const childrenIds = getChildrenIds(gantt, task.id);
    const hasVisibleDescendants = childrenIds.some(
      (childId) =>
        taskFilterResults.get(childId) || taskHasVisibleDescendants.get(childId)
    );

    taskHasVisibleDescendants.set(task.id, hasVisibleDescendants);
  });

  gantt.eachTask((task: Task) => {
    const taskPassesFilter = taskFilterResults.get(task.id) || false;
    const hasVisibleChildren = taskHasVisibleDescendants.get(task.id) || false;
    const isRootTask = task.parent === 0;
    task[marker] = !hasVisibleChildren && (isRootTask || !taskPassesFilter);
  });
};
